import React, { useState, useEffect } from "react";
import moment from "moment/moment.js";
import { DatePicker, Spin } from 'antd';
import { presets } from '../../utils/RangePickerPresets.js';
import Overview from "./components/Overview.jsx";
import AreaChart from "../../components/global/charts/AreaChart.jsx";
import { GetAllDashboardApi } from "../../api/request/dashboard.js";
import { GetAllDepositRequestApi } from "../../api/request/cashDeposit/depositRequest.js";
import { GetAllAccountApi } from "../../api/request/wallet/withdraw.js";
import Deposit from "../../assets/wallet/Deposit.jsx";
import Withdraw from "../../assets/wallet/Withdraw.jsx";
import Pending from "../../assets/common/Pending.jsx";
import Approved from "../../assets/common/Approved.jsx";
import Commission from "../../assets/common/Commission.jsx";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import Segmented from "../../components/segmented/Segmented.jsx";
import TransactionCard from "./components/TransactionCard.jsx";
import IbReqColumns from "../../columns/dashboard/IbReq.js";
import DepositColumns from "../../columns/dashboard/Deposit.js";
import WithdrawColumns from "../../columns/dashboard/Withdraw.js";

const { RangePicker } = DatePicker;

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(0);
  const [depositWithdrawDateRange, setDepositWithdrawDateRange] = useState({ fromDate: '', toDate: '' });
  const [depositWithdrawLoading, setDepositWithdrawLoading] = useState(false);
  const primaryColor = (getComputedStyle(document.documentElement).getPropertyValue('--theme'))?.trim();
  const secondary = getComputedStyle(document.documentElement).getPropertyValue('--theme2')?.trim();
  const [depositWithdrawalData, setDepositWithdrawalData] = useState([]);
  const [ibReqestData, setIbReqestData] = useState([])
  const [dashboardFatched, setDashboardFatched] = useState(false);
  const [value, setValue] = useState('Deposit');
  const [pendingRequestsData, setPendingRequestsData] = useState([
    { name: 'Deposit', count: 0, icon: <Deposit color="#28C76F" />, bgColor: "bg-[#E5F1F2]", color: '#28C76F', navigate: '/deposit_request' },
    { name: 'Withdraw', count: 0, icon: <Withdraw color="#FF5964" />, bgColor: "bg-[#FFE2E3]", color: '#FF5964', navigate: '/wallet_withdraw' },
  ]);

  const manageChartData = (data) => {
    let depositData = [];
    let withdrawData = [];
    let dateData = [];
    data?.map((item) => {
      depositData?.push(item?.totalDeposit);
      withdrawData?.push(item?.totalWithdrawal);
      dateData?.push(`${moment(item?.date).format('MMM DD')}`);
    });
    setDepositWithdrawalData({ categories: dateData, series: [{ name: 'Total Deposit', data: depositData, prefix: '$', postfix: '' }, { name: 'Total Withdraw', data: withdrawData, prefix: '$', postfix: '' }] });
  }

  const getAllDashboard = async () => {
    try {
      setIsLoading(true);
      let params = {
        FromDate: depositWithdrawDateRange?.fromDate,
        ToDate: depositWithdrawDateRange?.toDate,
      }
      const { data } = await GetAllDashboardApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setDashboardData(data?.data);
        manageChartData(data?.data?.depositWithdrawalList);
        setIbReqestData(data?.data?.ibrequestPendinglist)
      }
      setIsLoading(false);
      setDashboardFatched(true);
    } catch (error) {
      setIsLoading(false);
    }
  }

  const getDepositWithdrawData = async () => {
    try {
      setDepositWithdrawLoading(true);
      let params = {
        FromDate: depositWithdrawDateRange?.fromDate,
        ToDate: depositWithdrawDateRange?.toDate,
      }
      const { data } = await GetAllDashboardApi(new URLSearchParams(params).toString());
      if (data?.success) {
        manageChartData(data?.data?.depositWithdrawalList);
        setIbReqestData(data?.data?.ibrequestPendinglist)
      }
      setDepositWithdrawLoading(false);
    } catch (error) {
      setDepositWithdrawLoading(false);
    }
  }

  const getPendingRequests = async () => {
    try {
      const params = { FilterText: 'pending', ApplyPagination: false, SortBy: 'requestDate' };
      const [depositRes, withdrawRes] = await Promise.all([
        GetAllDepositRequestApi(new URLSearchParams(params).toString()),
        GetAllAccountApi(new URLSearchParams(params).toString()),
      ]);
      const depositCount = depositRes?.data?.success ? depositRes?.data?.totalRecords || 0 : 0;
      const withdrawCount = withdrawRes?.data?.success ? withdrawRes?.data?.totalRecords || 0 : 0;
      setPendingRequestsData([
        { name: 'Deposit', count: depositCount, icon: <Deposit color="#28C76F" />, bgColor: "bg-[#E5F1F2]", color: '#28C76F', navigate: '/deposit_request' },
        { name: 'Withdraw', count: withdrawCount, icon: <Withdraw color="#FF5964" />, bgColor: "bg-[#FFE2E3]", color: '#FF5964', navigate: '/wallet_withdraw' },
      ]);
    } catch (error) {
      setPendingRequestsData([
        { name: 'Deposit', count: 0, icon: <Deposit color="#28C76F" />, bgColor: "bg-[#E5F1F2]", color: '#28C76F', navigate: '/deposit_request' },
        { name: 'Withdraw', count: 0, icon: <Withdraw color="#FF5964" />, bgColor: "bg-[#FFE2E3]", color: '#FF5964', navigate: '/wallet_withdraw' },
      ]);
    }
  };

  useEffect(() => {
    getAllDashboard();
    getPendingRequests();
  }, []);

  useEffect(() => {
    if (dashboardFatched) {
      getDepositWithdrawData();
    }
  }, [depositWithdrawDateRange]);

  const handleDateChange = (value) => {
    if (value && value.length === 2) {
      const fromDate = value[0].format('MM/DD/YYYY');
      const toDate = value[1].format('MM/DD/YYYY');
      setDepositWithdrawDateRange({ fromDate: fromDate, toDate: toDate });
    }
    if (!value || value.length === 0) {
      setDepositWithdrawDateRange({ fromDate: '', toDate: '' });
    }
  };

  const disabled7DaysDate = (current, { from }) => {
    if (from) {
      return Math.abs(current.diff(from, 'days')) >= 7;
    }
    return false;
  };
  const transactionData = [
    {
      name: 'Deposit', count: dashboardData?.totalDeposit || 0, icon: <Deposit color="#28C76F" />, bgColor: "bg-[#E5F1F2]", color: '#28C76F',
      navigate: '/wallet_deposit'
    },
    {
      name: 'Withdraw', count: dashboardData?.totalWithdraw || 0, icon: <Withdraw color="#FF5964" />, bgColor: "bg-[#FFE2E3]", color: '#FF5964',
      navigate: '/wallet_withdraw'
    },
  ]

  const ftdData = [
    {
      name: 'Complete', count: dashboardData?.ftdDone || 0, icon: <Approved color="#28C76F" />, bgColor: "bg-[#DDF6E8]", color: '#28C76F',
      navigate: '/client?filter=All+Clients&ftd=Yes'
    },
    {
      name: 'Pending', count: dashboardData?.ftdPending || 0, icon: <Pending color="#FF9F43" />, bgColor: "bg-[#FFF0E1]", color: '#FF9F43',
      navigate: '/client?filter=All+Clients&ftd=No'
    },
  ]
  const ibReqData = [
    {
      name: 'Approved', count: dashboardData?.ibRequestApproved || 0, icon: <Approved color="#28C76F" />, bgColor: "bg-[#DDF6E8]", color: '#28C76F',
      navigate: '/ib_manager?filter=Approved'
    },
    {
      name: 'Pending', count: dashboardData?.ibRequestPending || 0, icon: <Pending color="#FF9F43" />, bgColor: "bg-[#FFF0E1]", color: '#FF9F43',
      navigate: '/ib_manager?filter=Pending'
    },
  ]
  const ibRCommissionData = [
    {
      name: 'Generated', count: dashboardData?.ibCommisionGen || 0, icon: <Commission color="#7367F0" />, bgColor: "bg-[#E9E7FD]", color: '#7367F0',
      navigate: '/ib_commission_generate'
    },
    {
      name: 'Withdraw', count: dashboardData?.ibCommisionWD || 0, icon: <Withdraw color="#00BAD1" />, bgColor: "bg-[#D6F4F8]", color: '#00BAD1',
      navigate: '/ib_commission_withdraw'
    },
  ]

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-5 mb-5">
            <Overview dashboardData={dashboardData} />

            <div className="col-span-6 order-2">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                <div className='border-2 border-light rounded-[10px] p-5 h-fit'>
                  <TransactionCard data={pendingRequestsData} title={"Pending Requests"} />
                </div>
                <div className='border-2 border-light rounded-[10px] p-5 h-fit'>
                  <TransactionCard data={transactionData} title={"Transactions"} />
                </div>
                <div className='border-2 border-light rounded-[10px] p-5 h-fit'>
                  <TransactionCard data={ibRCommissionData} title={"IB Commission"} />
                </div>
                <div className='border-2 border-light rounded-[10px] p-5 h-fit'>
                  <TransactionCard data={ibReqData} title={"IB Requests"} />
                </div>
                {/* <div className='border-2 border-light rounded-[10px] p-5 h-fit'>
                  <TransactionCard data={ftdData} title={"First Time Deposit"} />
                </div> */}
              </div>
            </div>

            <div className="xl:col-span-4 col-span-6 order-4 xl:order-3 border-2 border-light rounded-[10px] p-5">
              <div className="flex flex-wrap items-center justify-between gap-4 mx-4 mb-4">
                <div className="flex items-center flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <div className="h-[10px] w-[10px] bg-primary rounded-[50%]"></div>
                    <p className="font-medium text-[#07111CCC]">Deposit</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-[10px] w-[10px] bg-[var(--theme2)] rounded-[50%]"></div>
                    <p className="font-medium text-[#07111CCC]">Withdraw</p>
                  </div>
                </div>
                <div className='ant-date-range-selector-white'>
                  <RangePicker disabledDate={disabled7DaysDate} className='py-1' onChange={(value) => handleDateChange(value)} presets={presets} format='DD-MM-YYYY' />
                </div>
              </div>

              {
                depositWithdrawLoading ? (
                  <div className="flex justify-center flex-col my-[20%]">
                    <Spin size="large" />
                    <p className="primary text-center mt-2">Loading...</p>
                  </div>
                ) : (
                  <AreaChart data={depositWithdrawalData} colors={[primaryColor, secondary]} height={425} />
                )
              }
            </div>

            <div className='xl:col-span-2  col-span-6 order-3 xl:order-4 flex flex-col gap-5'>
              {/* <div className="border-2 border-light rounded-[10px] p-5">
                <h2 class="text-title font-semibold">IB Commissions</h2>
                <div className="flex items-center justify-between gap-5 mt-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`flex justify-center items-center w-[30px] h-[30px] rounded-lg bg-[#E9E7FD]`}>
                        <Commission color="#7367F0" size={5} />
                      </div>
                      <p>Generate</p>
                    </div>
                    <p className="text-2xl my-2">${dashboardData?.ibCommisionGen || 0}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`flex justify-center items-center w-[30px] h-[30px] rounded-lg bg-[#D6F4F8]`}>
                        <Withdraw color="#00BAD1" size={5} />
                      </div>
                      <p>Withdraw</p>
                    </div>
                    <p className="text-2xl my-2">${dashboardData?.ibCommisionWD || 0}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`flex justify-center items-center w-[30px] h-[30px] rounded-lg bg-[#FFF0E1]`}>
                        <ChartPie color="#FF9F43" size={5} />
                      </div>
                      <p>Admin</p>
                    </div>
                    <p className="text-2xl my-2">${dashboardData?.adminCommissionGen || 0}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="border-2 border-light rounded-[10px] p-5">
                  <h2 class="text-title font-semibold">Transactions</h2>
                  <div className="flex flex-col gap-5 mt-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#DDF6E8]`}>
                          <Deposit color="#28C76F" />
                        </div>
                        <div>
                          <p>Deposit</p>
                          <p className="text-sm text-gray-500">${dashboardData?.totalDeposit || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#FFE2E3]`}>
                          <Withdraw color="#FF4C51" />
                        </div>
                        <div>
                          <p>Withdraw</p>
                          <p className="text-sm text-gray-500">${dashboardData?.totalWithdraw || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-2 border-light rounded-[10px] p-5">
                  <h2 class="text-title font-semibold">First Time Deposit</h2>
                  <div className="flex flex-col gap-5 mt-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#DDF6E8]`}>
                          <Approved color="#28C76F" size={5} />
                        </div>
                        <div>
                          <p>Done</p>
                          <p className="text-sm text-gray-500">{dashboardData?.ftdDone || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#FFF0E1]`}>
                          <Pending color="#FF9F43" size={5} />
                        </div>
                        <div>
                          <p>Pending</p>
                          <p className="text-sm text-gray-500">{dashboardData?.ftdPending || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-2 border-light rounded-[10px] p-5">
                <h2 class="text-title font-semibold">IB Requests</h2>
                <div className="grid grid-cols-2 gap-5 mt-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#DDF6E8]`}>
                        <Approved color="#28C76F" size={5} />
                      </div>
                      <div>
                        <p>Approved</p>
                        <p className="text-sm text-gray-500">{dashboardData?.ibRequestApproved || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={`flex justify-center items-center w-[35px] h-[35px] rounded-lg bg-[#FFF0E1]`}>
                        <Pending color="#FF9F43" size={5} />
                      </div>
                      <div>
                        <p>Pending</p>
                        <p className="text-sm text-gray-500">{dashboardData?.ibRequestPending || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}

              <div className="border-2 border-light rounded-[10px] p-5 h-full">
                <h2 class="text-title font-semibold">Recent IB Requests</h2>

                <div className="mt-5">
                  <TableWithSkeleton columns={IbReqColumns} data={ibReqestData} loading={isLoading} />
                </div>
              </div>
            </div>

            <div className="col-span-6 order-5 border-2 border-light rounded-[10px] p-5">
              <div className="flex justify-between gap-5">
                <h2 class="text-title font-semibold">Recent Transactions</h2>
                <Segmented options={["Deposit", "Withdraw"]} value={value} setValue={setValue} />
              </div>

              <div className="mt-5">
                <TableWithSkeleton
                  columns={value === "Deposit" ? DepositColumns : WithdrawColumns}
                  data={value === "Deposit" ? dashboardData?.depositRequestApproved : dashboardData?.withdrawRequestApproved}
                  loading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>)}
    </div>
  );
};

export default Dashboard;